import { Application, Blocks } from '@/types/application';
import { Metadata, PortOption } from '@/types/metadata/metadata';
import { Semaphore } from '@/types/semaphores';
import { Signal } from '@/types/signal';
import { Subroutine } from '@/types/subroutine';
import { Severity, ValidationError } from '@/types/validation';
import { VariableRef, Variables, VariableScope } from '@/types/variable';
import { findSubroutine } from '@/util/application';
import { validateAppStartCount, validateBlocks } from './blocks';
import { validateSubroutines } from './subroutines';

export const validateApplication = (
  application: Application,
  metadata: Metadata,
  updateNodes: string[]
): ValidationError[] => {
  const errors: ValidationError[] = [];

  if (!application) {
    errors.push({ severity: 'error', message: 'Application is undefined.' });
    return errors;
  }
  if (!application.name) {
    errors.push({ severity: 'error', message: 'Application is missing a name.' });
  }
  if (!application.label) {
    errors.push({
      severity: 'warning',
      message: 'Application is missing label.'
    });
  }
  if (application.signals) {
    const signalErrors = validateSignals(application.signals, application.subroutines);
    if (signalErrors.length) errors.push(...signalErrors);
  }

  if (application.semaphores) {
    const semaphoreErrors = validateSemaphores(application.semaphores);
    if (semaphoreErrors.length) errors.push(...semaphoreErrors);
  }

  const appStartErrors = validateAppStartCount(application, metadata);
  if (appStartErrors.length) errors.push(...appStartErrors);

  const subroutineErrors = validateSubroutines(
    application.blocks,
    application.subroutines,
    application.variables,
    metadata
  );
  if (subroutineErrors.length) errors.push(...subroutineErrors);

  const blockErrors = validateBlocks(application, metadata, updateNodes);
  if (blockErrors.length) errors.push(...blockErrors);

  return errors;
};

export const validateSignals = (signals: Signal[] | undefined, subroutines: Subroutine[]): ValidationError[] => {
  const errors: ValidationError[] = [];
  signals?.map((signal) => {
    if (!subroutineExists(subroutines, signal.substart)) {
      if (signal.substart === undefined) {
        errors.push({
          severity: 'warning',
          message: `Signal ${signal.name} (${signal.id}) doesn't link to a subroutine.`
        });
      } else {
        errors.push({
          severity: 'warning',
          message: `Signal ${signal.name} (${signal.id}) refers to a subroutine (${signal.substart}) that does not exist.`
        });
      }
    }
    if (!signal.name) {
      errors.push({
        severity: 'error',
        message: `Signal ID ${signal.id} is missing a name.`
      });
    }
  });
  return errors;
};

export const validateSemaphores = (semaphores?: Semaphore[]): ValidationError[] => {
  const errors: ValidationError[] = [];
  semaphores?.map((semaphore) => {
    if (!semaphore.name) {
      errors.push({
        severity: 'error',
        message: `Semaphore ID ${semaphore.id} is missing a name.`
      });
    }
  });
  return errors;
};

export const validatePort = (
  id: number,
  portoptionIndex: number,
  metadataPorts: PortOption[]
): ValidationError[] | undefined => {
  if (metadataPorts[portoptionIndex]?.type === 'nothing') {
    return [
      {
        severity: 'error',
        message: `Port is set to a nonexistant port option.`,
        id: id
      }
    ] as ValidationError[];
  }
};

export const blockTypeExists = (metadata: Metadata, type: string) =>
  Boolean(metadata.blocks.some((block) => block.type === type));

export const blockExists = (blocks: Blocks, blockId: number) => Boolean(blocks.nodes[blockId]);

export const subroutineExists = (subroutines: Subroutine[], subroutineId: number): boolean =>
  Boolean(findSubroutine(subroutines, subroutineId));

export const signalExists = (application: Application, signalId: number): boolean =>
  Boolean(application.signals && application.signals.some((signal) => signal.id === signalId));

export const variableExists = (
  variables: Variables,
  subroutines: Subroutine[],
  metadata: Metadata,
  variable: VariableRef,
  subroutineId?: number
): boolean => {
  if (!variable) return false;
  // handle local scope
  variable.scope = variable.scope.toLowerCase() as VariableScope;
  if (variable.scope === 'local') {
    if (subroutineId && subroutineExists(subroutines, subroutineId)) {
      const subVars = variables.local?.[subroutineId];
      if (!subVars) return false;
      return Boolean(subVars?.find((subvar) => subvar.name === variable.name));
    }
    return false;
  }
  // handle all other scopes
  const varsInScope = variables[variable.scope];
  const varsInMetaScope = metadata.variables?.[variable.scope];

  if (!varsInScope && !varsInMetaScope) return false;
  const varInApp = varsInScope?.find((varInScope) => varInScope.name === variable.name);
  const varInMeta = varsInMetaScope?.find((varInMetaScope) => varInMetaScope.name === variable.name);
  return Boolean(varInApp || varInMeta);
};

const severityOrder: Severity[] = ['info', 'warning', 'error'];

export const getMostSevereLevel = (errors: ValidationError[]): Severity => {
  const mostSevereLevel = errors.reduce<Severity>((acc, curr) => {
    return severityOrder.indexOf(curr.severity) > severityOrder.indexOf(acc) ? curr.severity : acc;
  }, 'info');
  return mostSevereLevel;
};
